import React, { useState, useEffect } from 'react';
import { FaWpforms, FaCheckCircle } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  TimeScale, // Import TimeScale
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns'; // Import the adapter

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  TimeScale, // Register TimeScale
);

const API_URL = "https://back-desalpe.onrender.com/api";

const InvitationStats = () => {
    console.log("InvitationStats component rendering."); // New log
    const [stats, setStats] = useState({ total: 0, confirmed: 0 });
    const [anonymousClickCount, setAnonymousClickCount] = useState(0);
    const [invitationsByDate, setInvitationsByDate] = useState([]); // New state for time-series data
    const [clicksByDate, setClicksByDate] = useState([]); // New state for time-series data
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('day'); // 'day', 'month', 'year'
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0')); // '01' to '12'

    useEffect(() => {
        console.log("InvitationStats useEffect triggered."); // New log
        const fetchStats = async () => {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            console.log("InvitationStats: Token retrieved from localStorage:", token ? "Exists" : "Does NOT exist"); // New log

            if (!token) {
                console.error("No auth token found. Cannot fetch stats.");
                setLoading(false);
                return;
            }

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            // Construct query parameters
            let queryParams = '';
            let filterValue = '';
            if (filterType === 'month') {
                filterValue = `${selectedYear}-${selectedMonth}`;
                queryParams = `?type=${filterType}&value=${filterValue}`;
            } else if (filterType === 'year') {
                filterValue = selectedYear;
                queryParams = `?type=${filterType}&value=${filterValue}`;
            }

            console.log("InvitationStats: About to make API calls with Promise.all."); // New log

            try {
                const [invitationResponse, anonymousClickResponse, invitationsByDateResponse, clicksByDateResponse] = await Promise.all([
                    (async () => {
                        try {
                            const res = await fetch(`${API_URL}/invitations/stats`, { headers });
                            if (!res.ok) {
                                console.error('InvitationStats: /invitations/stats fetch failed with status:', res.status, await res.text());
                            }
                            return res;
                        } catch (err) {
                            console.error('InvitationStats: Error fetching /invitations/stats:', err);
                            throw err; // Re-throw to propagate to outer catch
                        }
                    })(),
                    (async () => {
                        try {
                            const res = await fetch(`${API_URL}/anonymous-clicks/invitation-confirmation/count`, { headers });
                            if (!res.ok) {
                                console.error('InvitationStats: /anonymous-clicks/invitation-confirmation/count fetch failed with status:', res.status, await res.text());
                            }
                            return res;
                        } catch (err) {
                            console.error('InvitationStats: Error fetching /anonymous-clicks/invitation-confirmation/count:', err);
                            throw err;
                        }
                    })(),
                    (async () => {
                        try {
                            const res = await fetch(`${API_URL}/invitations/stats/by-date${queryParams}`, { headers });
                            if (!res.ok) {
                                console.error('InvitationStats: /invitations/stats/by-date fetch failed with status:', res.status, await res.text());
                            }
                            return res;
                        } catch (err) {
                            console.error('InvitationStats: Error fetching /invitations/stats/by-date:', err);
                            throw err;
                        }
                    })(),
                    (async () => {
                        try {
                            const res = await fetch(`${API_URL}/anonymous-clicks/invitation-confirmation/by-date${queryParams}`, { headers });
                            if (!res.ok) {
                                console.error('InvitationStats: /anonymous-clicks/invitation-confirmation/by-date fetch failed with status:', res.status, await res.text());
                            }
                            return res;
                        } catch (err) {
                            console.error('InvitationStats: Error fetching /anonymous-clicks/invitation-confirmation/by-date:', err);
                            throw err;
                        }
                    })(),
                ]);

                // Log each response status
                console.log('InvitationStats: /invitations/stats response status:', invitationResponse.status, invitationResponse.ok);
                console.log('InvitationStats: /anonymous-clicks/invitation-confirmation/count response status:', anonymousClickResponse.status, anonymousClickResponse.ok);
                console.log('InvitationStats: /invitations/stats/by-date response status:', invitationsByDateResponse.status, invitationsByDateResponse.ok);
                console.log('InvitationStats: /anonymous-clicks/invitation-confirmation/by-date response status:', clicksByDateResponse.status, clicksByDateResponse.ok);
                
                // Check if responses are OK before parsing JSON
                if (!invitationResponse.ok) console.error('InvitationStats: /invitations/stats fetch failed:', await invitationResponse.text());
                if (!anonymousClickResponse.ok) console.error('InvitationStats: /anonymous-clicks/invitation-confirmation/count fetch failed:', await anonymousClickResponse.text());
                if (!invitationsByDateResponse.ok) console.error('InvitationStats: /invitations/stats/by-date fetch failed:', await invitationsByDateResponse.text());
                if (!clicksByDateResponse.ok) console.error('InvitationStats: /anonymous-clicks/invitation-confirmation/by-date fetch failed:', await clicksByDateResponse.text());

                const invitationData = await invitationResponse.json();
                const anonymousClickData = await anonymousClickResponse.json();
                const invitationsByDateData = await invitationsByDateResponse.json();
                const clicksByDateData = await clicksByDateResponse.json();

                // Log each parsed data
                console.log('InvitationStats: /invitations/stats data:', invitationData);
                console.log('InvitationStats: /anonymous-clicks/invitation-confirmation/count data:', anonymousClickData);
                console.log('InvitationStats: /invitations/stats/by-date data:', invitationsByDateData);
                console.log('InvitationStats: /anonymous-clicks/invitation-confirmation/by-date data:', clicksByDateData);

                setStats(invitationData);
                setAnonymousClickCount(anonymousClickData.count);
                setInvitationsByDate(invitationsByDateData);
                setClicksByDate(clicksByDateData);

            } catch (error) {
                console.error("Erreur lors de la récupération des statistiques:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [filterType, selectedYear, selectedMonth]);

    // Prepare data for the time-series chart
    const allDates = [...new Set([
        ...invitationsByDate.map(d => d.date),
        ...clicksByDate.map(d => d.date)
    ])].sort();

    const invitationsDataForChart = allDates.map(date => {
        const found = invitationsByDate.find(d => d.date === date);
        return found ? found.count : 0;
    });

    const clicksDataForChart = allDates.map(date => {
        const found = clicksByDate.find(d => d.date === date);
        return found ? found.count : 0;
    });

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Évolution des Inscriptions et Confirmations par Date',
            },
            tooltip: {
                callbacks: {
                    title: function(context) {
                        const dateLabel = context[0].label;
                        if (filterType === 'year') return dateLabel;
                        if (filterType === 'month') return new Date(dateLabel).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
                        return new Date(dateLabel).toLocaleDateString();
                    },
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += context.parsed.y;
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: filterType === 'year' ? 'year' : (filterType === 'month' ? 'month' : 'day'),
                    displayFormats: {
                        day: 'MMM dd',
                        month: 'MMM yyyy',
                        year: 'yyyy'
                    },
                    tooltipFormat: 'PPP' // Pretty date format for tooltips
                },
                title: {
                    display: true,
                    text: 'Date',
                },
                grid: {
                    display: false // Hide vertical grid lines
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Nombre',
                },
                beginAtZero: true,
                ticks: {
                    precision: 0 // Ensure whole numbers for counts
                }
            },
        },
    };

    const chartData = {
        labels: allDates,
        datasets: [
            {
                label: 'Formulaires Remplis',
                data: invitationsDataForChart,
                backgroundColor: 'rgba(52, 152, 219, 0.7)',
                borderColor: 'rgba(52, 152, 219, 1)',
                tension: 0.3,
                fill: false,
                pointRadius: 5, // Make points more visible
                pointHoverRadius: 7,
            },
            {
                label: 'Clics de Confirmation de Présence',
                data: clicksDataForChart,
                backgroundColor: 'rgba(46, 204, 113, 0.7)',
                borderColor: 'rgba(46, 204, 113, 1)',
                tension: 0.3,
                fill: false,
                pointRadius: 5, // Make points more visible
                pointHoverRadius: 7,
            },
        ],
    };

    if (loading) {
        return <div>Chargement des statistiques...</div>;
    }

    return (
        <div className="invitation-stats">
            <div className="stats-summary" style={{ marginTop: '20px' }}>
                <div className="stat-card">
                    <FaWpforms size={40} style={{ marginBottom: '15px', color: '#3498db' }} />
                    <h4>Formulaires Remplis</h4>
                    <p>{stats.total}</p>
                </div>
                <div className="stat-card">
                    <FaCheckCircle size={40} style={{ marginBottom: '15px', color: '#2ecc71' }} />
                    <h4>Clics de Confirmation de Présence</h4>
                    <p>{anonymousClickCount}</p>
                </div>
            </div>

            <div className="filter-bar" style={{ marginBottom: '20px' }}>
                <button onClick={() => setFilterType('day')} className={filterType === 'day' ? 'active' : ''}>Jour</button>
                <button onClick={() => setFilterType('month')} className={filterType === 'month' ? 'active' : ''}>Mois</button>
                <button onClick={() => setFilterType('year')} className={filterType === 'year' ? 'active' : ''}>Année</button>

                {filterType === 'month' && (
                    <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                        {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map(month => (
                            <option key={month} value={month}>{month}</option>
                        ))}
                    </select>
                )}
                {(filterType === 'month' || filterType === 'year') && (
                    <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                        {Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString()).map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                )}
            </div>

            <div className="graph-container" style={{ marginTop: '40px' }}>
                <Line options={chartOptions} data={chartData} />
            </div>
        </div>
    );
};

export default InvitationStats;